import { clsx } from "clsx"
import { memo, useCallback, useMemo } from "react"
import { useMutation, useQueryClient } from "react-query"
import styled from "styled-components"
import { AppState, useAppDispatch, useAppSelector } from "reducers/store"
import { showToast } from "reducers/toast/toast-actions"
import { validateName } from "utils/auth/authHelper"
import Button from "components/EvtKit/button/Button"
import UserArea from "../UserArea"
import ApiAccounts from "api/ApiAccounts"
import { USER_PERSONAL_INFO } from "reducers/user/actions-types"

const ContainerEdit = styled.div`
  display: grid;
  gap: 10px;
  align-items: flex-start;
`
const ButtonEdit = styled.button`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  & button {
    height: 40px;
    width: 60px;
    padding: 0 40px;
  }
`

const NameInput = styled.input`
  &:focus {
    outline: none;
  }

  max-width: 200px;
  max-height: 48px;
`

export interface EditProfile {
  name: string
}

interface Props {
  setEditing: React.Dispatch<React.SetStateAction<boolean>>
  editedValue: EditProfile
  setEditedValue: React.Dispatch<React.SetStateAction<EditProfile>>
}

const UserEdit = (props: Props) => {
  const queryClient = useQueryClient()
  const appDispatch = useAppDispatch()
  const { name } = useAppSelector((x: AppState) => x.user.userPersonalInfo.data)

  const { setEditing, editedValue, setEditedValue } = props

  const mutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      await new ApiAccounts().updateProfileName(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PERSONAL_INFO] })
      appDispatch(showToast("Os dados foram salvos com sucesso!"))
    },
    onError: () => {
      appDispatch(showToast("Não foi possível salvar os dados. Tente novamente.", "error"))
    },
    retry: 0
  })

  const isNameValid = useMemo(() => {
    const maxNameLength = 80
    const validateLength = editedValue.name.length < maxNameLength

    return validateLength && validateName(editedValue.name)
  }, [editedValue.name])

  const handleInputName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value

      setEditedValue({ name: newName })
    },
    [setEditedValue]
  )

  const isSaveButtonDisabled = useMemo(() => {
    const editedNameWithoutSpaces = editedValue.name.replace(/\s/g, "").toLowerCase()
    const currentNameWithoutSpaces = name.replace(/\s/g, "").toLowerCase()

    return !isNameValid || editedNameWithoutSpaces === currentNameWithoutSpaces || editedValue.name.trim() === ""
  }, [editedValue.name, name, isNameValid])

  const handleSaveClick = useCallback(() => {
    if (!isSaveButtonDisabled) {
      mutation.mutate(editedValue)
    }

    setEditing(false)
  }, [mutation, editedValue, setEditing, appDispatch, isSaveButtonDisabled])

  const handleCancelClick = useCallback(() => {
    setEditing(false)
    if (name) {
      setEditedValue({ name: name })
    }
  }, [setEditing, setEditedValue, name])

  return (
    <ContainerEdit>
      <UserArea mainText="Nome Completo" />
      <NameInput type="text" id="name-space" value={editedValue.name} onChange={handleInputName} />
      <ButtonEdit>
        <Button onClick={handleSaveClick} disabled={isSaveButtonDisabled} className="isSmall">
          Salvar
        </Button>
        <Button onClick={handleCancelClick} className={clsx("premium", "isSmall")}>
          Cancelar
        </Button>
      </ButtonEdit>
    </ContainerEdit>
  )
}

export default memo(UserEdit)
